/**
 * Non-Redux request-context store — the transport read source for the
 * NON-TOKEN request-config inputs (auth-redux-elimination, transport-boundary
 * slice). It is the config sibling of `tokenStore`: `getRequestConfig()`
 * derives hosts, brand headers, and the platform flag from here instead of
 * `store.getState()`, so the query seam carries no Redux import.
 *
 * ## Field sources
 *
 * - `envConfig` / `brandConfig`: on the BROWSER these are the SSR-injected
 *   `__APP_CONFIG__` / `__BRAND_CONFIG__` JSON script tags (the same source
 *   `src/redux/clientStore.ts` seeds `state.session` from), parsed lazily on
 *   first read and cached — they never change within a page load. On the
 *   SERVER they are per-request (brand is chosen by request hostname), so
 *   reads resolve through the same pluggable context-resolver pattern as
 *   `tokenStore`: `requestContext.server.ts` installs an
 *   AsyncLocalStorage-backed resolver and `src/server.ts` seeds a snapshot
 *   per request. This module stays browser-safe (no `async_hooks` import).
 *
 * - `pathname`: feeds only the `X-Extend-Platform` header ('mira' on CSP
 *   surfaces). Browser reads are live from `window.location` (the source
 *   `connected-react-router` mirrors into `state.router.location`); the
 *   server sees the request URL's pathname from the per-request snapshot.
 *
 * - `isCspAuthenticated`: NOT derivable from `tokenStore.getCspJwt()` — SSR
 *   seeds the cookie'd `cspJwt` while `state.auth.isCspAuthenticated` stays
 *   false, and the reducer flips the flag independently of the token. On the
 *   browser Redux remains the write source and mirrors the flag in via
 *   `requestContext.interop.ts` (wired in `src/client.tsx`), exactly like the
 *   token mirror. The server snapshot pins it to the same initial-state
 *   `false` the per-request Redux store was seeded with.
 *
 * Reactivity: like `tokenStore`, this store is intentionally NON-reactive.
 * It is a call-time read source for building axios configs — do not put its
 * getters in React dependency arrays.
 */
import type { BrandConfig, EnvConfig } from 'src/configurations'

export type RequestContextSnapshot = {
  envConfig: EnvConfig
  brandConfig: BrandConfig
  pathname: string
  isCspAuthenticated: boolean
}

/**
 * Server-side resolver indirection. The default returns `undefined` (browser,
 * or no request context active), so reads fall through to the browser
 * sources below. The server overrides this once at boot via
 * `setRequestContextResolver`.
 */
let resolveContext: () => RequestContextSnapshot | undefined = () => undefined

export const setRequestContextResolver = (
  resolver: () => RequestContextSnapshot | undefined,
): void => {
  resolveContext = resolver
}

const parseConfigElement = <T>(id: string): T =>
  JSON.parse(
    (typeof document !== 'undefined' &&
      document.getElementById(id)?.innerHTML) ||
      '{}',
  ) as T

/**
 * Browser-side state. Config is parsed from the DOM once on first read;
 * `seedConfig` (tests, or an explicit boot seed) takes precedence over the
 * DOM parse. The CSP flag defaults to `false` — the same value a freshly
 * seeded Redux store holds — until the interop mirror first syncs.
 */
let browserEnvConfig: EnvConfig | undefined
let browserBrandConfig: BrandConfig | undefined
let browserIsCspAuthenticated = false

export const requestContext = {
  getEnvConfig: (): EnvConfig =>
    resolveContext()?.envConfig ??
    (browserEnvConfig ??= parseConfigElement<EnvConfig>('__APP_CONFIG__')),

  getBrandConfig: (): BrandConfig =>
    resolveContext()?.brandConfig ??
    (browserBrandConfig ??= parseConfigElement<BrandConfig>('__BRAND_CONFIG__')),

  getPathname: (): string =>
    resolveContext()?.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : ''),

  getIsCspAuthenticated: (): boolean =>
    resolveContext()?.isCspAuthenticated ?? browserIsCspAuthenticated,

  /** Interop mirror write (browser). Writes the active context when one is installed. */
  setIsCspAuthenticated: (isCspAuthenticated: boolean): void => {
    const context = resolveContext()
    if (context !== undefined) {
      context.isCspAuthenticated = isCspAuthenticated
    } else {
      browserIsCspAuthenticated = isCspAuthenticated
    }
  },

  /** Override the DOM-parsed config (test seam). */
  seedConfig: (config: {
    envConfig: EnvConfig
    brandConfig: BrandConfig
  }): void => {
    browserEnvConfig = config.envConfig
    browserBrandConfig = config.brandConfig
  },

  /** Reset every browser-side field (test seam). */
  reset: (): void => {
    browserEnvConfig = undefined
    browserBrandConfig = undefined
    browserIsCspAuthenticated = false
  },
}
